import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  type ArchNode,
  type EdgeKind,
  ARCH_LAYERS,
  edgesFor,
  getNode,
} from "@/data/architecture";

interface ProtocolMapPanelProps {
  node: ArchNode;
  onClose: () => void;
  onNavigate: (nodeId: string) => void;
  /** Called when the user clicks the schema-domain deep-link. */
  onSchemaDeepLink?: (schemaDomainId: string) => void;
}

const EDGE_KIND_LABEL: Record<EdgeKind, string> = {
  "anchors-to": "anchors to",
  enforces: "enforces",
  "stored-in": "stored in",
  governs: "governs",
};

/**
 * Pinned detail card for the focused node. Shows the node's full prose,
 * an inventory of incoming/outgoing edges (each navigable), and a
 * deep-link into SchemaExplorer when the node maps to a schema domain.
 */
export function ProtocolMapPanel({
  node,
  onClose,
  onNavigate,
  onSchemaDeepLink,
}: ProtocolMapPanelProps) {
  const layer = ARCH_LAYERS.find((l) => l.id === node.layer);
  const { incoming, outgoing } = edgesFor(node.id);

  return (
    <div
      role="region"
      aria-label={`${node.label} — detail`}
      className="border border-hairline bg-paper-2 p-6 md:p-8 flex flex-col gap-5"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-2">
            {layer?.title ?? node.layer}
            {node.planned && (
              <span className="ml-2 text-state-pending border border-state-pending/50 px-1 py-px tracking-widest">
                planned
              </span>
            )}
          </div>
          <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight">
            {node.label}
          </h3>
          {node.caption && (
            <p className="font-serif italic text-ink-2 mt-1">{node.caption}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close detail"
          className="font-mono text-xs uppercase tracking-widest text-ink-3 hover:text-ink border border-hairline hover:border-ink px-3 py-1.5 transition-colors"
        >
          close
        </button>
      </div>

      {/* Description */}
      {node.description && (
        <p className="font-serif text-ink-2 leading-relaxed text-base md:text-lg max-w-2xl">
          {node.description}
        </p>
      )}

      {/* Edge inventory */}
      {(incoming.length > 0 || outgoing.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-hairline">
          {outgoing.length > 0 && (
            <EdgeList
              heading="Connects to"
              edges={outgoing.map((e) => ({
                kind: e.kind,
                otherId: e.to,
                note: e.note,
              }))}
              onNavigate={onNavigate}
            />
          )}
          {incoming.length > 0 && (
            <EdgeList
              heading="Held up by"
              edges={incoming.map((e) => ({
                kind: e.kind,
                otherId: e.from,
                note: e.note,
              }))}
              onNavigate={onNavigate}
            />
          )}
        </div>
      )}

      {/* Cross-references */}
      <div className="flex flex-wrap items-center gap-3 pt-2">
        {node.schemaDomainId && onSchemaDeepLink && (
          <button
            type="button"
            onClick={() => onSchemaDeepLink(node.schemaDomainId!)}
            className="text-xs uppercase tracking-widest font-semibold text-ink border border-ink px-4 py-2 hover:bg-ink hover:text-paper transition-colors"
          >
            Open <span className="font-mono normal-case tracking-normal">{node.schemaDomainId}</span> in schema
          </button>
        )}
        {node.href && (
          <Link
            to={node.href}
            className="text-xs uppercase tracking-widest font-semibold text-ink underline decoration-hairline underline-offset-4 hover:decoration-ink"
          >
            Read more →
          </Link>
        )}
      </div>
    </div>
  );
}

interface EdgeListEntry {
  kind: EdgeKind;
  otherId: string;
  note?: string;
}

function EdgeList({
  heading,
  edges,
  onNavigate,
}: {
  heading: string;
  edges: EdgeListEntry[];
  onNavigate: (nodeId: string) => void;
}) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-3">
        {heading}
      </div>
      <ul role="list" className="space-y-2">
        {edges.map((e, i) => {
          const other = getNode(e.otherId);
          if (!other) return null;
          return (
            <li key={`${e.kind}-${e.otherId}-${i}`} className="text-sm">
              <button
                type="button"
                onClick={() => onNavigate(e.otherId)}
                className={cn(
                  "text-left w-full group flex flex-col gap-0.5 py-1.5 px-2 -mx-2",
                  "border border-transparent hover:border-hairline hover:bg-paper transition-colors",
                )}
              >
                <span className="flex items-baseline gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3 shrink-0">
                    {EDGE_KIND_LABEL[e.kind]}
                  </span>
                  <span className="font-mono text-[12px] text-ink truncate">
                    {other.label}
                  </span>
                </span>
                {e.note && (
                  <span className="font-serif italic text-[12px] text-ink-2 leading-snug pl-[88px] -mt-0.5 hidden md:block">
                    {e.note}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
